// Heuristic per-message height estimator used by `MessageListScrollStrategy`
// as the fallback height when ResizeObserver hasn't measured a row yet.
// Phase 2a goal: replace the static 80 px default with a smart per-message
// estimate so the cascade adjustment when a row's real height lands is
// tiny (< ~10 px) instead of substantial (often hundreds of px).
//
// Pure function — no DOM, no Angular, no signals. Takes the message + the
// current viewport context and returns an approximate rendered row height
// in pixels. Mirrors the layout in `chat-msg.component.html` / `.scss`;
// any change to that template's height contributors must be reflected
// here. The estimator is intentionally simple — it doesn't aim for pixel
// accuracy (the real DOM measurement still refines via ResizeObserver),
// it just needs to be in the right ballpark so the strategy's initial
// prefix-sum is plausible.
//
// Sizing constants below are derived from `chat-msg.component.scss` and
// `chat.component.scss`. They assume the browser's root font size is 16 px
// (the project default).
import { Message } from '../../../state/messages.repository';

// ────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────

const REM = 16;
const LINE_HEIGHT_BODY = Math.round(REM * 1.4); // 22 — body text
const LINE_HEIGHT_SMALL = Math.round(REM * 0.8 * 1.4); // ~18 — 0.8rem font

// .message-row
const ROW_PADDING_BOTTOM = 0.5 * REM; // 8

// .text-container (the bubble)
const BUBBLE_PADDING_V = 2 * 0.5 * REM; // 16 (top+bot)
const BUBBLE_BORDER_V = 2; // 1px each side
const BUBBLE_CHROME_V = BUBBLE_PADDING_V + BUBBLE_BORDER_V; // 18

// .user-info (header row above message text — shown when not bundled or
// when this row is the first in a bundle).
const HEADER_HEIGHT = LINE_HEIGHT_SMALL + 0.5 * REM; // ~26

// .quote-preview (reply snippet). Two lines (sender + snippet) but the
// snippet is single-line ellipsed via white-space:nowrap, so it's actually
// one line of header + one line of snippet = ~36 px total including
// padding + margin-bottom.
const QUOTE_HEIGHT = 36;

// .reactions-row (chip strip below the bubble content).
const REACTIONS_HEIGHT = 30;

// Bundled non-first messages get `margin-top: -0.3rem` to visually
// compress against the previous bundled row. Account for the overlap so
// the prefix-sum reflects the screen layout.
const BUNDLE_OVERLAP = -0.3 * REM; // -4.8

// Embed card chrome (`.embed-url`).
const EMBED_PADDING_V = 2 * 0.5 * REM; // 16
const EMBED_MARGIN_TOP = 0.5 * REM; // 8
const EMBED_TITLE_HEIGHT = LINE_HEIGHT_BODY; // ~22
const EMBED_GAP = 0.2 * REM; // 3.2

// Image attachment box constraints.
const IMG_WRAPPER_MAX_WIDTH = 20 * REM; // 320
const IMG_MAX_HEIGHT = 15 * REM; // 240

// Embed image box constraints.
const EMBED_MAX_WIDTH = 25 * REM; // 400
const EMBED_IMG_MAX_HEIGHT = 15 * REM; // 240

// Special-kind row heights — system / poke / mute render as dividers or
// short single-line bubbles. Heights are roughly fixed.
const SYSTEM_ROW_HEIGHT = 32;
// File card (`andor-room-file-card`): three text lines in a padded card.
const FILE_CARD_ROW_HEIGHT = 76;
const POKE_MUTE_ROW_HEIGHT = 52;

// Estimator fallback when a kind is completely unknown.
const DEFAULT_FALLBACK_HEIGHT = 80;

// Average glyph width in px at body font size. Real fonts vary (capital W
// is wide, lowercase i is narrow), and our font stack mixes serif/sans
// fallbacks. ~7.5 px is a sane mean for English prose at 16 px.
const AVG_CHAR_PX = 7.5;
// Monospace glyphs are uniformly wider.
const MONO_CHAR_PX = 9.6;
// Code-block rendering uses `font: monospace; font-size: 13px;
// line-height: normal`. "normal" resolves to ~1.3 in most browsers,
// giving roughly 17 px per line of code — substantially tighter than the
// body line-height. Without this distinction the estimator over-counts
// the height of tall code blocks by ~5 px per line, which compounds
// fast on a 50-line fixture excerpt (~250 px over).
const MONO_LINE_HEIGHT = 17;

// ────────────────────────────────────────────────────────────────────────
// Public entry point
// ────────────────────────────────────────────────────────────────────────

/**
 * Returns the estimated rendered height of the given message row in pixels,
 * for the given viewport width. The chat layout mode ('left' vs 'split') is
 * deliberately not an input — it only flips alignment/margins/corners and
 * doesn't change any height contributor.
 *
 * Used by the strategy as the per-row default when ResizeObserver hasn't
 * fed a real measurement yet. The estimate isn't meant to be exact — the
 * cascade refinement still corrects it once the row paints — but a tight
 * estimate keeps the cascade jump small enough that it's imperceptible.
 */
export function estimateMessageHeight(message: Message, viewportWidth: number): number {
  switch (message.kind) {
    case 'message':
      if (message.origin === 'system') {
        return SYSTEM_ROW_HEIGHT + ROW_PADDING_BOTTOM;
      }
      return estimateUserMessageHeight(message, viewportWidth);
    case 'poke':
    case 'mute':
      return POKE_MUTE_ROW_HEIGHT + ROW_PADDING_BOTTOM;
    case 'file':
      return FILE_CARD_ROW_HEIGHT + ROW_PADDING_BOTTOM;
    default:
      return DEFAULT_FALLBACK_HEIGHT;
  }
}

// ────────────────────────────────────────────────────────────────────────
// Internals — user-message height composition
// ────────────────────────────────────────────────────────────────────────

function estimateUserMessageHeight(message: Message, viewportWidth: number): number {
  if (message.kind !== 'message') return DEFAULT_FALLBACK_HEIGHT;
  const payload = message.payload;
  const innerWidth = bubbleInnerWidth(viewportWidth);
  let h = ROW_PADDING_BOTTOM + BUBBLE_CHROME_V;

  // Header row — hidden for non-first bundled rows.
  const showHeader = !message.isBundled || !!message.isFirstInBundle;
  if (showHeader) h += HEADER_HEIGHT;

  // Quote preview (reply snippet) when this row is a reply.
  if (payload?.quotedMessage) h += QUOTE_HEIGHT;

  // Image attachment.
  if (payload?.attachment?.type === 'image') {
    h += estimateImageAttachmentHeight(payload.attachment, innerWidth);
  }

  // Body text — `payload.text` is the raw markdown source now that
  // rendering moved to display time, so the estimator can walk it
  // directly without any HTML-stripping fallback.
  const text = payload?.text;
  if (text) h += estimateTextHeight(text, innerWidth);

  // Embed card (OGP-resolved link preview).
  if (payload?.embedUrl) h += estimateEmbedHeight(payload.embedUrl, innerWidth);

  // Reactions chip row.
  if ((message.reactions?.length ?? 0) > 0) h += REACTIONS_HEIGHT;

  // Bundled non-first rows tuck up against the previous one — subtract
  // the overlap so the cumulative prefix-sum matches what the screen
  // actually shows.
  if (message.isBundled && !message.isFirstInBundle) h += BUNDLE_OVERLAP;

  return Math.max(0, Math.round(h));
}

// ────────────────────────────────────────────────────────────────────────
// Internals — per-component contributors
// ────────────────────────────────────────────────────────────────────────

function bubbleInnerWidth(viewportWidth: number): number {
  // `.text-container { max-width: calc(92% - 2.5rem); }` — percentage
  // resolves against the flex container (.message) which fills the row
  // (which fills the viewport since the wrapper is pinned to width:100%).
  // Subtract bubble padding + border for the inner content width.
  const bubbleOuter = Math.max(0, 0.92 * viewportWidth - 2.5 * REM);
  return Math.max(40, bubbleOuter - BUBBLE_CHROME_V);
}

function estimateImageAttachmentHeight(
  attachment: { width?: number; height?: number },
  bubbleInnerW: number
): number {
  const wrapperMaxW = Math.min(IMG_WRAPPER_MAX_WIDTH, bubbleInnerW);
  if (attachment.width && attachment.height) {
    const aspect = attachment.height / attachment.width;
    const renderedW = Math.min(wrapperMaxW, attachment.width);
    return Math.round(Math.min(IMG_MAX_HEIGHT, renderedW * aspect));
  }
  // No dims — pre-Phase-1 attachments. Use a reasonable middle-of-the-road
  // height; the cascade refinement still corrects it on first paint.
  return 200;
}

function estimateEmbedHeight(
  embed: {
    title: string;
    description?: string;
    image?: string;
    imageWidth?: number;
    imageHeight?: number;
  },
  bubbleInnerW: number
): number {
  const cardOuter = Math.min(EMBED_MAX_WIDTH, bubbleInnerW);
  const cardInner = Math.max(40, cardOuter - 2 * 0.5 * REM);

  let h = EMBED_MARGIN_TOP + EMBED_PADDING_V;

  // Title (typically 1 line, sometimes wraps).
  const titleLines = wrapLines(embed.title, cardInner, AVG_CHAR_PX);
  h += titleLines * EMBED_TITLE_HEIGHT;

  if (embed.description) {
    h += EMBED_GAP;
    const descLines = wrapLines(embed.description, cardInner, AVG_CHAR_PX);
    h += descLines * LINE_HEIGHT_BODY;
  }

  if (embed.image) {
    h += EMBED_GAP;
    if (embed.imageWidth && embed.imageHeight) {
      const aspect = embed.imageHeight / embed.imageWidth;
      const renderedW = Math.min(cardInner, embed.imageWidth);
      h += Math.min(EMBED_IMG_MAX_HEIGHT, renderedW * aspect);
    } else {
      // Pre-Phase-1.5 embed without dims. Use a reasonable estimate.
      h += 180;
    }
  }

  return Math.round(h);
}

function estimateTextHeight(text: string, widthPx: number): number {
  if (!text) return 0;
  // Track fenced-code-block state across lines. Inside a fence:
  //   - Lines render monospace with `white-space: pre`, so they don't wrap
  //     in the chat-msg template — each source line is exactly one
  //     rendered line. (The `.scroller` overflow gives them horizontal
  //     scrolling instead of wrapping when they're wider than the bubble.)
  //   - Fence markers themselves (` ```ts `, ` ``` `) render as part of
  //     the code-block's `controls` slot, not as separate body lines.
  // Outside a fence: each hard line wraps proportionally to bubble width.
  const hardLines = text.split('\n');
  let totalH = 0;
  let inFence = false;
  for (const line of hardLines) {
    if (line.startsWith('```')) {
      // Fence marker — toggle state, contribute the fence-row chrome
      // (small button/copy label area) only on the opener.
      if (!inFence) totalH += MONO_LINE_HEIGHT;
      inFence = !inFence;
      continue;
    }
    if (inFence) {
      // One source line = one rendered monospace line, no wrapping.
      totalH += MONO_LINE_HEIGHT;
    } else {
      totalH += wrapLines(line, widthPx, AVG_CHAR_PX) * LINE_HEIGHT_BODY;
    }
  }
  return totalH;
}

function wrapLines(text: string, widthPx: number, avgCharPx: number): number {
  if (!text) return 1;
  const charsPerLine = Math.max(1, Math.floor(widthPx / avgCharPx));
  return Math.max(1, Math.ceil(text.length / charsPerLine));
}
